import React, { useState } from "react";
import axios from "axios";
import {
  Form,
  FormGroup,
  FormLabel,
  FormControl,
  Button,
} from "react-bootstrap";
import { useFormik } from "formik";
import { FiEye, FiEyeOff } from "../../../../icons";
import { motion, AnimatePresence } from "framer-motion";
import MainLogo from "../../../../images/main-logo.svg";
import { Link, useNavigate } from "react-router-dom";
import LoginSlider from "../LoginSlider/LoginSlider.tsx";
import LoginImage from "../../../../images/login-image.png";
import AuthLeftSection from "../AuthLeftSection/AuthLeftSection.tsx";
import AuthBackground from "../AuthBackground/AuthBackground.tsx";
import apiConfig from "../../config/apiConfig.js";
import { loginSchema } from '../utils/validationSchema.js';
import { sanitizers } from '../utils/inputSanitizers.js';
import { API_ENDPOINTS } from '../utils/constants.js';
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./Login.scss";

function Login() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);

 const formik = useFormik({
  initialValues: {
    email: '',
    password: '',
  },
  validationSchema: loginSchema,
 onSubmit: async (values, { setSubmitting }) => {
  try {
    const response = await axios.post(
      `${apiConfig.apiBaseURL}${API_ENDPOINTS.LOGIN}`,
      {
        email: values.email.trim(),
        password: values.password,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (response.data.status === "Success") {
      localStorage.setItem("accountStatus", response.data.accountStatus);
      if (response.data.token) {
        localStorage.setItem("authToken", response.data.token);
      }
      toast.success(response.data.message || 'Login successful! Welcome back.');
      
      // Navigate based on accountStatus
      if (response.data.accountStatus === "Inactive") {
        navigate('/subscription');
      } else {
        navigate('/my-gifts');
      }
    } else {
      toast.error(response.data.message || 'Login failed. Please try again.');
    }
  } catch (error) {
    toast.error(error.response?.data?.message || 'Login failed. Please try again.');
  } finally {
    setSubmitting(false);
  }
}
});

  // Add this function to handle validation and submit
  const handleValidateAndSubmit = async () => {
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      // Just mark fields as touched to show validation errors below fields
      // No need for toast as Formik already shows errors below each field
      formik.setTouched({ email: true, password: true });
      return;
    }
    formik.handleSubmit();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === " ") {
      e.preventDefault();
    }
  };

  // Animation variants for smooth, calm transitions
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut",
        staggerChildren: 0.2
      }
    }
  };

  const slideInLeft = {
    hidden: { opacity: 0, x: -60, scale: 0.95 },
    visible: {
      opacity: 1,
      x: 0,
      scale: 1,
      transition: {
        duration: 1,
        ease: [0.25, 0.46, 0.45, 0.94] // Calm easing curve
      }
    }
  };

  const slideInRight = {
    hidden: { opacity: 0, x: 60, scale: 0.95 },
    visible: {
      opacity: 1,
      x: 0,
      scale: 1,
      transition: {
        duration: 1,
        ease: [0.25, 0.46, 0.45, 0.94],
        delay: 0.3
      }
    }
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  const floatingAnimation = {
    animate: {
      y: [0, -8, 0],
      transition: {
        duration: 3,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  const buttonHover = {
    scale: 1.02,
    transition: { duration: 0.2, ease: "easeInOut" }
  };

  const inputFocus = {
    scale: 1.01,
    boxShadow: "0 8px 30px rgba(57, 87, 160, 0.12)",
    transition: { duration: 0.3, ease: "easeOut" }
  };

  return (
    <>
      {/* Full-screen background animations */}
      <AuthBackground />

      <motion.section
        className="login-screen"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px 15px', position: 'relative', zIndex: 1 }}
      >
        <div style={{ maxWidth: '1600px', width: '100%' }}>
        <div className="row" style={{ width: '100%', margin: '0' }}>
        {/* Left Panel - Image and Content */}
        <AuthLeftSection
          image={LoginImage}
          imageAlt="Login"
          title="A Legacy of Love, Connection and<br/>Comfort"
          description="The bonds we share should transcend life's final chapter, continuing to bring comfort, joy and connection in the moments that matter most."
          slideInLeft={slideInLeft}
          fadeInUp={fadeInUp}
          floatingAnimation={floatingAnimation}
        />

        {/* Right Panel - Login Form */}
        <div className="col-md-6" style={{ display: 'flex' }}>
          {/* OUTER WRAPPER - Has background and padding */}
          <motion.div
            className="login-screen-right-wrapper"
            variants={slideInRight}
            initial="hidden"
            animate="visible"
            style={{
              height: 'calc(100vh - 20px)',
              width: '100%',
              background: '#F2F3F7',
              borderRadius: '24px'
            }}
          >
            {/* INNER SCROLLABLE CONTAINER */}
            <div
              className="login-screen-right"
              style={{
                height: '100%',
                overflowY: 'auto',
                width: '100%',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <div className="login-screen-right-in" style={{ background: 'transparent' }}>
            <motion.div
              className="middle-block"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.8,
                ease: "easeOut",
                delay: 0.6
              }}
            >
              <motion.figure
                className="main-logo"
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{
                  duration: 0.8,
                  ease: "easeOut",
                  delay: 0.8
                }}
                whileHover={{ scale: 1.05 }}
              >
                <img src={MainLogo} alt="Logo" />
              </motion.figure>

              <motion.h3
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.6,
                  ease: "easeOut",
                  delay: 1
                }}
                style={{ marginBottom: '2rem', color: '#2c3e50' }}
              >
                Login to your account
              </motion.h3>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.2, duration: 0.6 }}
              >
                <Form onSubmit={formik.handleSubmit} className="py-0">
                  {/* Email Field */}
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 1.4, duration: 0.6 }}
                  >
                    <FormGroup className="form-group">
                      <FormLabel>Email *</FormLabel>
                      <motion.div whileFocus={inputFocus}>
                        <FormControl
                          autoFocus
                          type="email"
                          name="email"
                          placeholder="Email address"
                          value={formik.values.email}
                          onChange={(e) => {
                            const sanitizedEmail = sanitizers.email(e.target.value);
                            formik.setFieldValue("email", sanitizedEmail);
                          }}
                          className={`form-control p-3`}
                          autoComplete="off"
                          onKeyDown={handleKeyDown}
                          disabled={formik.isSubmitting}
                          style={{
                            border: '2px solid #e0e6ed',
                            borderRadius: '12px',
                            transition: 'all 0.3s ease'
                          }}
                        />
                      </motion.div>
                      <AnimatePresence>
                        {formik.touched.email && formik.errors.email && (
                          <motion.small
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            style={{
                              color: "#dc3545",
                              display: "block",
                              marginTop: "8px",
                              fontSize: "0.875rem",
                            }}
                          >
                            {formik.errors.email}
                          </motion.small>
                        )}
                      </AnimatePresence>
                    </FormGroup>
                  </motion.div>

                  {/* Password Field */}
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 1.6, duration: 0.6 }}
                  >
                    <FormGroup className="form-group password-field">
                      <FormLabel>Password *</FormLabel>
                      <motion.div whileFocus={inputFocus} style={{ position: 'relative' }}>
                        <FormControl
                          type={showPassword ? "text" : "password"}
                          name="password"
                          placeholder="Password"
                          value={formik.values.password}
                          onChange={formik.handleChange}
                          className={`form-control p-3`}
                          autoComplete="new-password"
                          onKeyDown={handleKeyDown}
                          disabled={formik.isSubmitting}
                          style={{
                            border: '2px solid #e0e6ed',
                            borderRadius: '12px',
                            transition: 'all 0.3s ease',
                            paddingRight: '45px'
                          }}
                        />
                        <span
                          onClick={() => setShowPassword(!showPassword)}
                          className="password-toggle-icon"
                          style={{
                            position: 'absolute',
                            right: '15px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            cursor: 'pointer',
                            userSelect: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#677289',
                            zIndex: 10
                          }}
                        >
                          {showPassword ? (
                            <FiEyeOff size={20} />
                          ) : (
                            <FiEye size={20} />
                          )}
                        </span>
                      </motion.div>
                      <AnimatePresence>
                        {formik.touched.password && formik.errors.password && (
                          <motion.small
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            style={{
                              color: "#dc3545",
                              display: "block",
                              marginTop: "8px",
                              fontSize: "0.875rem",
                              lineHeight: "1.4",
                            }}
                          >
                            {formik.errors.password}
                          </motion.small>
                        )}
                      </AnimatePresence>
                    </FormGroup>
                  </motion.div>

                  {/* Login Button */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.8, duration: 0.6 }}
                    whileHover={buttonHover}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Button
                      className="main-bttn"
                      type="submit"
                      onClick={handleValidateAndSubmit}
                      disabled={formik.isSubmitting}
                      style={{
                        width: '100%',
                        padding: '14px 20px',
                        background: formik.isSubmitting
                          ? 'linear-gradient(135deg, #bbb, #999)'
                          : '#D7A360',
                        border: 'none',
                        borderRadius: '12px',
                        fontSize: '16px',
                        fontWeight: '600',
                        boxShadow: '0 8px 20px rgba(215, 163, 96, 0.3)',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <AnimatePresence mode="wait">
                        {formik.isSubmitting ? (
                          <motion.span
                            key="loading"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                          >
                            <motion.span
                              animate={{ rotate: 360 }}
                              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                              style={{ display: 'inline-block', marginRight: '8px' }}
                            >
                              ⟳
                            </motion.span>
                            Logging in...
                          </motion.span>
                        ) : (
                          <motion.span
                            key="login"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                          >
                            Login
                          </motion.span>
                        )}
                      </AnimatePresence>
                    </Button>
                  </motion.div>

                  <motion.div
                    className="forgot-link"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 2, duration: 0.6 }}
                  >
                    <motion.div whileHover={{ scale: 1.02 }}>
                      <Link
                        to="/forgot"
                        style={{
                          color: '#3957A0',
                          textDecoration: 'none',
                          display: 'inline-block',
                          fontWeight: '600',
                          fontSize: '18px'
                        }}
                      >
                        Forgot Password?
                      </Link>
                    </motion.div>
                  </motion.div>

                  <motion.div
                    className="account"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 2.2, duration: 0.6 }}
                    style={{ textAlign: 'center', marginTop: '1.5rem' }}
                  >
                    Don't have an account?{" "}
                    <motion.span whileHover={{ scale: 1.05 }}>
                      <Link
                        to="/signup"
                        style={{
                          color: '#3957A0',
                          textDecoration: 'none',
                          fontWeight: '600'
                        }}
                      >
                        Sign up
                      </Link>
                    </motion.span>
                  </motion.div>
                </Form>
              </motion.div>
            </motion.div>
          </div>
          </div>
          </motion.div>
        </div>
      </div>
      </div>
      </motion.section>
    </>
  );
}

export default Login;
